import React from 'react';

export const renderMarkdown = (content: string): React.ReactNode => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeBlockLanguage = '';
  let codeBlockContent: string[] = [];
  let listItems: string[] = [];
  let inList = false;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside mb-4 space-y-2 text-stone-700 ml-4">
          {listItems.map((item, idx) => (
            <li key={idx} className="mb-1">
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };

  const flushCodeBlock = () => {
    if (codeBlockContent.length > 0) {
      const code = codeBlockContent.join('\n');
      elements.push(
        <pre key={`code-${elements.length}`} className="bg-stone-900 text-stone-100 p-4 rounded-lg overflow-x-auto my-4">
          <code className={`language-${codeBlockLanguage} text-sm font-mono`}>{code}</code>
        </pre>
      );
      codeBlockContent = [];
      codeBlockLanguage = '';
      inCodeBlock = false;
    }
  };

  const renderInlineMarkdown = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    
    // Handle bold **text**
    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;
    let lastIndex = 0;
    
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(<strong key={`bold-${currentIndex++}`} className="font-semibold text-stone-900">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    // Handle inline code `code`
    const codeRegex = /`([^`]+)`/g;
    const finalParts: React.ReactNode[] = [];
    let codeLastIndex = 0;
    
    parts.forEach((part, idx) => {
      if (typeof part === 'string') {
        let codeMatch;
        while ((codeMatch = codeRegex.exec(part)) !== null) {
          if (codeMatch.index > codeLastIndex) {
            finalParts.push(part.substring(codeLastIndex, codeMatch.index));
          }
          finalParts.push(
            <code key={`inline-code-${idx}-${codeMatch.index}`} className="bg-stone-100 px-1.5 py-0.5 rounded text-sm font-mono text-amber-700">
              {codeMatch[1]}
            </code>
          );
          codeLastIndex = codeMatch.index + codeMatch[0].length;
        }
        if (codeLastIndex < part.length) {
          finalParts.push(part.substring(codeLastIndex));
        }
        codeLastIndex = 0;
      } else {
        finalParts.push(part);
      }
    });
    
    return finalParts.length > 0 ? <>{finalParts}</> : text;
  };

  lines.forEach((line, index) => {
    // Handle code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock();
      } else {
        flushList();
        codeBlockLanguage = line.substring(3).trim();
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      return;
    }

    // Handle headings
    if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h1 key={`h1-${index}`} className="text-4xl font-bold text-stone-900 mb-6 mt-0 pb-4 border-b border-stone-200">
          {renderInlineMarkdown(line.substring(2))}
        </h1>
      );
      return;
    }

    if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={`h2-${index}`} className="text-3xl font-bold text-stone-800 mb-4 mt-8">
          {renderInlineMarkdown(line.substring(3))}
        </h2>
      );
      return;
    }

    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={`h3-${index}`} className="text-2xl font-semibold text-stone-700 mb-3 mt-6">
          {renderInlineMarkdown(line.substring(4))}
        </h3>
      );
      return;
    }

    if (line.startsWith('#### ')) {
      flushList();
      elements.push(
        <h4 key={`h4-${index}`} className="text-xl font-semibold text-stone-700 mb-2 mt-4">
          {renderInlineMarkdown(line.substring(5))}
        </h4>
      );
      return;
    }

    // Handle list items
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      if (!inList) {
        flushList();
        inList = true;
      }
      listItems.push(line.trim().substring(2));
      return;
    }

    // Handle numbered lists
    const numberedListMatch = line.match(/^\d+\.\s+(.+)$/);
    if (numberedListMatch) {
      if (!inList) {
        flushList();
        inList = true;
      }
      listItems.push(numberedListMatch[1]);
      return;
    }

    // Flush list if we hit a non-list line
    if (inList && line.trim() !== '') {
      flushList();
    }

    // Handle empty lines
    if (line.trim() === '') {
      if (!inList) {
        elements.push(<br key={`br-${index}`} />);
      }
      return;
    }

    // Handle regular paragraphs
    elements.push(
      <p key={`p-${index}`} className="text-stone-700 mb-4 leading-relaxed text-base">
        {renderInlineMarkdown(line)}
      </p>
    );
  });

  flushList();
  flushCodeBlock();

  return <>{elements}</>;
};

